/**
 * Cadastre parcel overlay for the electronic field map (admin-only API).
 *
 * Polygons use geojson-vt canvas tiles (same approach as FieldManager) to avoid
 * thousands of Leaflet SVG paths and related memory pressure.
 */
import { Injectable, NgZone, inject } from '@angular/core';
import geojsonvt from 'geojson-vt';
import {clearLeafletDataPane} from './gis-vt-pane-cleanup.util';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { OpenStreetMapComponent } from '@components/openstreetmap/openstreetmap.component';
import {
  CadastreParcelBoundingBoxRequest,
  CadastreParcelService,
  CadastreParcelMapDto,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { extractCoordinatesFromWKT } from '@app/utils/wkt-parser';
import { PolygonConverterUtil } from '@app/utils/polygon-converter.util';
import { findFeatureAtPoint, latLngToTileCoords, latLngToTilePixel } from '@app/utils/vector-tile.utils';
import { MapClickEvent, MapMoveEvent, Marker } from '@components/openstreetmap/interfaces';
import { OSM_POLYGON_FILL_VISIBILITY_EVENT } from '@components/openstreetmap/constants/map-polygon-fill-visibility.state';
import { escapePopupHtml } from './gis-popup-html.util';
import { filterByViewportBounds } from './gis-map-viewport-filter.util';
import { buildVtOriginalFeaturesByNumericId } from './gis-vt-feature-index.util';
import { paintVtPolygonOnCanvasTile, repaintLeafletGridLayerCanvases } from './gis-vt-polygon-canvas.util';

declare const L: any;

type CadastreParcelMapView = CadastreParcelMapDto & {
  districtSoato?: string | null;
};

const CADASTRE_FILL = 'rgba(255, 152, 0, 0.35)';
const CADASTRE_STROKE = 'rgba(230, 81, 0, 0.95)';
const CADASTRE_VT_TILE_EXTENT = 4096;
const CADASTRE_VIEWPORT_BOUNDS_PAD = 0.08;
const CADASTRE_GEOJSON_VT_OPTIONS = {
  maxZoom: 18,
  extent: CADASTRE_VT_TILE_EXTENT,
  buffer: 64,
  lineMetrics: false,
  promoteId: 'parcelVtId' as const,
  generateId: false,
  indexMaxZoom: 5,
  indexMaxPoints: 100000,
} as const;

@Injectable({ providedIn: 'root' })
export class CadastreParcelManager {
  private readonly cadastreParcelService = inject(CadastreParcelService);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);

  private mapInstance: OpenStreetMapComponent | null = null;
  private accentColor = CADASTRE_FILL;
  private strokeColor = CADASTRE_STROKE;
  private fillOpacity = 0.35;
  /** Leaflet pane for geojson-vt cadastre polygons (General GIS may override per layer stack). */
  cadastrePolygonsPane = 'cadastrePolygonsPane';
  /** Named markercluster group for multi-layer GIS. Empty = default group. */
  clusterGroupId: string | null = null;
  /** Leaflet pane for clustered cadastre markers (General GIS uses one pane per layer stack rank). */
  markerClusterPane: string | null = null;

  private cadastreVtLayer: any = null;
  private managedMarkerIds = new Set<string>();
  private cadastreVtPointer: {
    tileIndex: any;
    featuresById: Map<number, any>;
  } | null = null;
  private cadastreVtThinStroke = false;
  private cadastreVtOpId = 0;

  private parcelsDisplaySub?: Subscription;
  private cadastreVtMapClickSub?: Subscription;
  private mapViewportRedrawSub?: Subscription;
  private polygonFillLeafletMap: any = null;

  public parcels$ = new BehaviorSubject<CadastreParcelMapView[]>([]);
  public parcelPolygonClick$ = new Subject<{ lat: number; lng: number; data: CadastreParcelMapView }>();

  private readonly onOsmPolygonFillVisibility = (): void => {
    try {
      this.cadastreVtLayer?.redraw?.();
    } catch {
      /* ignore */
    }
  };

  setAccentColor(color: string): void {
    if (!color) {
      return;
    }
    this.strokeColor = color;
    this.accentColor = color;
    this.updateDisplay();
  }

  setFillOpacity(opacityPercent: number): void {
    this.fillOpacity = Math.max(0, Math.min(100, Number(opacityPercent) || 0)) / 100;
    this.repaintCadastreVtCanvases();
  }

  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapViewportRedrawSub?.unsubscribe();
    this.mapViewportRedrawSub = undefined;

    this.mapInstance = mapInstance;
    this.setupViewportRedrawListeners();

    const leafletMap = mapInstance.getMapInstance?.();
    if (this.polygonFillLeafletMap && this.polygonFillLeafletMap !== leafletMap) {
      try {
        this.polygonFillLeafletMap.off(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
      } catch {
        /* ignore */
      }
    }
    this.polygonFillLeafletMap = leafletMap ?? null;
    try {
      leafletMap?.on?.(OSM_POLYGON_FILL_VISIBILITY_EVENT, this.onOsmPolygonFillVisibility);
    } catch {
      /* ignore */
    }

    if (!this.parcelsDisplaySub) {
      this.parcelsDisplaySub = this.parcels$.subscribe(() => {
        this.updateDisplay();
      });
    }

    if (!this.cadastreVtMapClickSub && mapInstance?.mapClick$) {
      this.cadastreVtMapClickSub = mapInstance.mapClick$.subscribe((event: MapClickEvent) =>
        this.handleCadastreVtMapClick(event),
      );
    }
  }

  loadParcels(params: CadastreParcelBoundingBoxRequest): Observable<CadastreParcelMapView[]> {
    const cadastreFilters = this.buildCadastreFilters(params.query?.filters);

    const request: CadastreParcelBoundingBoxRequest = {
      ...params,
      query: {
        ...(params.query ?? {}),
        filters: cadastreFilters as never,
      },
    };

    return this.cadastreParcelService
      .cropMonitoringCadastreParcelGetParcelsByCoordinatesPost(request)
      .pipe(
        tap((parcels) => {
          this.parcels$.next((parcels ?? []) as CadastreParcelMapView[]);
        }),
        map((parcels) => (parcels ?? []) as CadastreParcelMapView[]),
      );
  }

  getTotalCount(filters: unknown): Observable<number> {
    return this.cadastreParcelService.cropMonitoringCadastreParcelGetTotalCountPost({
      filters: this.buildCadastreFilters(filters) as never,
    });
  }

  clearCadastreLayers(): void {
    this.clearVisualLayersOnly();
    this.parcels$.next([]);
  }

  buildParcelPopupHtml(parcel: CadastreParcelMapView): string {
    const cadNumber = escapePopupHtml(parcel.cadNumber || '—');
    const area =
      parcel.area != null && !Number.isNaN(Number(parcel.area))
        ? `${Number(parcel.area).toFixed(2)} ha`
        : '—';
    const district = parcel.districtSoato
      ? escapePopupHtml(parcel.districtSoato)
      : '';
    const address = parcel.address ? escapePopupHtml(parcel.address) : '';
    const landType = parcel.landType ? escapePopupHtml(parcel.landType) : '';

    let html = `<b>${cadNumber}</b>`;
    html += `<br><small>${escapePopupHtml(this.translate.instant('AREA'))}: ${area}</small>`;
    if (district) {
      html += `<br><small>${escapePopupHtml(this.translate.instant('District SOATO'))}: ${district}</small>`;
    }
    if (landType) {
      html += `<br><small>${landType}</small>`;
    }
    if (address) {
      html += `<br><small>${address}</small>`;
    }
    return html;
  }

  private setupViewportRedrawListeners(): void {
    if (!this.mapInstance || this.mapViewportRedrawSub) {
      return;
    }

    const m = this.mapInstance;
    const sub = new Subscription();

    if (m.mapMove$) {
      sub.add(
        m.mapMove$.pipe(debounceTime(120)).subscribe((ev: MapMoveEvent) => {
          void ev;
          this.updateDisplay();
        }),
      );
    }

    this.mapViewportRedrawSub = sub;
  }

  private buildCadastreFilters(filters: unknown): Array<{
    filterField?: string;
    filterOperator?: number;
    filterValue?: unknown;
  }> {
    if (!Array.isArray(filters)) {
      return [];
    }

    const allowed = new Set([
      'regionId',
      'RegionId',
      'cadNumber',
      'CadNumber',
      'districtSoato',
      'DistrictSoato',
      'area',
      'Area',
      'cadastreCadNumber',
      'cadastreDistrictSoato',
      'cadastreArea',
    ]);
    return filters
      .filter((filter) => allowed.has(String(filter?.filterField ?? '')))
      .map((filter) => {
        const field = String(filter?.filterField ?? '');
        if (field === 'cadastreCadNumber') {
          return { ...filter, filterField: 'cadNumber' };
        }
        if (field === 'cadastreDistrictSoato') {
          return { ...filter, filterField: 'districtSoato' };
        }
        if (field === 'cadastreArea') {
          return {
            ...filter,
            filterField: 'area',
            filterOperator:
              filter.filterOperator == null || filter.filterOperator === 0
                ? 3 // GreaterThanOrEqual
                : filter.filterOperator,
          };
        }
        return filter;
      });
  }

  private updateDisplay(): void {
    if (!this.mapInstance) {
      return;
    }

    this.clearVisualLayersOnly();

    const allParcels = this.parcels$.value ?? [];
    const parcels = this.filterParcelsByViewport(allParcels);
    if (!parcels.length) {
      return;
    }

    this.renderCadastrePolygonsVectorTiles(parcels);
    this.renderCadastreMarkers(parcels);
  }

  private filterParcelsByViewport(parcels: CadastreParcelMapView[]): CadastreParcelMapView[] {
    return filterByViewportBounds(
      parcels,
      this.mapInstance?.getMapInstance?.(),
      CADASTRE_VIEWPORT_BOUNDS_PAD,
      (parcel) => this.getParcelRepresentativeLatLng(parcel),
    );
  }

  private getParcelRepresentativeLatLng(parcel: CadastreParcelMapView): unknown | null {
    const rings = this.geometryJsonToLatLngRings(parcel.geometryJson);
    const coordinates = rings[0];
    if (!coordinates?.length) {
      return null;
    }
    const c = PolygonConverterUtil.getPolygonCenter(coordinates);
    if (!c || typeof L === 'undefined' || typeof L.latLng !== 'function') {
      return null;
    }
    return L.latLng(c.lat, c.lng);
  }

  private clearVisualLayersOnly(): void {
    this.removeCadastreVectorTileLayer();
    if (this.mapInstance) {
      const markerIds = Array.from(this.managedMarkerIds);
      if (markerIds.length) {
        this.mapInstance.mapController$.next({ removeMarkers: markerIds });
      }
    }
    this.managedMarkerIds.clear();
  }

  /** Centroid markers with popup (same interaction model as FieldManager). */
  private renderCadastreMarkers(parcels: CadastreParcelMapView[]): void {
    if (!this.mapInstance) {
      return;
    }

    const markers = parcels
      .map((parcel) => this.createMarkerFromParcel(parcel))
      .filter((marker): marker is NonNullable<typeof marker> => marker !== null);

    markers.forEach((marker) => this.managedMarkerIds.add(marker.id));

    if (markers.length) {
      this.mapInstance.mapController$.next({ mergeMarkers: markers });
    }
  }

  private createMarkerFromParcel(parcel: CadastreParcelMapView): Marker | null {
    if (parcel?.id == null) {
      return null;
    }
    const rings = this.geometryJsonToLatLngRings(parcel.geometryJson);
    const coordinates = rings[0];
    if (!coordinates?.length) {
      return null;
    }
    const center = PolygonConverterUtil.getPolygonCenter(coordinates);
    if (!center) {
      return null;
    }

    const markerId = `cadastre_parcel_${parcel.id}`;
    const lat = center.lat;
    const lng = center.lng;

    return {
      id: markerId,
      lat,
      lng,
      title: parcel.cadNumber || '—',
      popup: this.buildParcelPopupHtml(parcel),
      openPopupOnHover: true,
      data: { parcel },
      color: this.accentColor,
      withCluster: true,
      clusterGroupId: this.clusterGroupId ?? undefined,
      clusterGroupAccent: this.clusterGroupId ? this.accentColor : undefined,
      ...(this.markerClusterPane ? { pane: this.markerClusterPane } : {}),
      onClick: () => {
        this.parcelPolygonClick$.next({
          lat,
          lng,
          data: parcel,
        });
      },
    };
  }

  private removeCadastreVectorTileLayer(): void {
    this.cadastreVtOpId++;
    clearLeafletDataPane(this.mapInstance?.getMapInstance?.(), this.cadastrePolygonsPane);
    this.cadastreVtLayer = null;
    this.cadastreVtPointer = null;
    this.cadastreVtThinStroke = false;
  }

  private ensureCadastreVtPane(map: any): void {
    const name = this.cadastrePolygonsPane;
    if (!map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.pointerEvents = 'none';
    }
  }

  private buildCadastreFeatureCollection(parcels: CadastreParcelMapView[]): GeoJSON.FeatureCollection | null {
    const features: GeoJSON.Feature[] = [];

    for (const parcel of parcels) {
      if (parcel?.id == null) {
        continue;
      }
      const ringsList = this.geometryJsonToLatLngRings(parcel.geometryJson);
      ringsList.forEach((coordinates, partIndex) => {
        if (!PolygonConverterUtil.isValidPolygon(coordinates)) {
          return;
        }

        const ringLngLat = coordinates.map(([lat, lng]) => [lng, lat] as [number, number]);
        const first = ringLngLat[0];
        const last = ringLngLat[ringLngLat.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          ringLngLat.push([first[0], first[1]]);
        }

        features.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [ringLngLat],
          },
          properties: {
            parcelVtId: this.parcelVtNumericId(parcel.id, partIndex),
            parcelIdStr: String(parcel.id),
            parcelPartIndex: partIndex,
            fillColor: this.accentColor,
            strokeColor: this.strokeColor,
            fillOpacity: this.fillOpacity,
            strokeWidth: 2,
            _parcel: parcel,
          },
        });
      });
    }

    if (!features.length) {
      return null;
    }

    return { type: 'FeatureCollection', features };
  }

  private renderCadastrePolygonsVectorTiles(parcels: CadastreParcelMapView[]): void {
    const leafletMap = this.mapInstance?.getMapInstance?.();
    if (!leafletMap) {
      return;
    }

    const fc = this.buildCadastreFeatureCollection(parcels);
    if (!fc?.features?.length) {
      return;
    }

    const opId = ++this.cadastreVtOpId;
    const featureCount = fc.features.length;
    const mgr = this;

    this.ngZone.runOutsideAngular(() => {
      try {
        const tolerance = featureCount > 20000 ? 5 : 3;
        const tileIndex = geojsonvt(fc as never, {
          maxZoom: CADASTRE_GEOJSON_VT_OPTIONS.maxZoom,
          extent: CADASTRE_GEOJSON_VT_OPTIONS.extent,
          buffer: CADASTRE_GEOJSON_VT_OPTIONS.buffer,
          lineMetrics: CADASTRE_GEOJSON_VT_OPTIONS.lineMetrics,
          promoteId: CADASTRE_GEOJSON_VT_OPTIONS.promoteId,
          generateId: CADASTRE_GEOJSON_VT_OPTIONS.generateId,
          indexMaxZoom: CADASTRE_GEOJSON_VT_OPTIONS.indexMaxZoom,
          indexMaxPoints: CADASTRE_GEOJSON_VT_OPTIONS.indexMaxPoints,
          tolerance,
        });

        const featuresById = buildVtOriginalFeaturesByNumericId(fc.features as never[], 'parcelVtId');
        const layer = mgr.createCadastreVectorGridLayer(tileIndex, featureCount);
        const vtPtr = { tileIndex, featuresById };

        mgr.ngZone.run(() => {
          if (opId !== mgr.cadastreVtOpId) {
            return;
          }
          const map = mgr.mapInstance?.getMapInstance?.();
          if (!map) {
            return;
          }
          mgr.ensureCadastreVtPane(map);
          map.addLayer(layer);
          mgr.cadastreVtLayer = layer;
          mgr.cadastreVtPointer = vtPtr;
        });
      } catch (e) {
        console.warn('CadastreParcelManager: geojson-vt rendering failed', e);
      }
    });
  }

  private createCadastreVectorGridLayer(tileIndex: any, featureCount: number): any {
    this.cadastreVtThinStroke = featureCount > 20000;
    const mgr = this;

    const VectorTileLayer = L.GridLayer.extend({
      createTile: (coords: any) => {
        const tile = document.createElement('canvas');
        const tileSize = 256;
        tile.width = tileSize;
        tile.height = tileSize;
        tile.style.pointerEvents = 'none';
        const ctx = tile.getContext('2d');
        if (!ctx) {
          return tile;
        }
        mgr.paintCadastreVtTile(ctx, tileIndex, coords);
        return tile;
      },
    });

    return new VectorTileLayer({
      pane: this.cadastrePolygonsPane,
      tileSize: 256,
      updateWhenZooming: false,
      updateWhenIdle: true,
      keepBuffer: 2,
    });
  }

  private paintCadastreVtTile(
    ctx: CanvasRenderingContext2D,
    tileIndex: any,
    coords: { x: number; y: number; z: number },
  ): void {
    const tileData = tileIndex.getTile(coords.z, coords.x, coords.y);
    if (!tileData?.features?.length) {
      return;
    }
    const scale = 256 / CADASTRE_VT_TILE_EXTENT;
    for (const feature of tileData.features) {
      ctx.save();
      paintVtPolygonOnCanvasTile(
        ctx,
        feature,
        scale,
        this.cadastreVtThinStroke,
        this.strokeColor,
        this.accentColor,
        this.fillOpacity,
      );
      ctx.restore();
    }
  }

  private repaintCadastreVtCanvases(): void {
    const tileIndex = this.cadastreVtPointer?.tileIndex;
    if (!this.cadastreVtLayer || !tileIndex) {
      return;
    }
    repaintLeafletGridLayerCanvases(this.cadastreVtLayer, (ctx, coords) => {
      this.paintCadastreVtTile(ctx, tileIndex, coords);
    });
  }

  private parcelVtNumericId(parcelId: unknown, partIndex: number): number {
    const raw = parcelId as unknown;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return ((raw * 1000) + partIndex) >>> 0 || 1;
    }
    const s = `${String(raw ?? '')}:${partIndex}`;
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    const id = Math.abs(h) >>> 0;
    return id || 1;
  }

  private queryParcelVtAt(lat: number, lng: number): CadastreParcelMapView | null {
    const vt = this.cadastreVtPointer;
    if (!vt || !this.mapInstance) {
      return null;
    }

    const leafletMap = this.mapInstance.getMapInstance?.();
    if (!leafletMap) {
      return null;
    }

    const zoom = Math.floor(leafletMap.getZoom());
    const tc = latLngToTileCoords(lat, lng, zoom);
    const tileData = vt.tileIndex.getTile(tc.z, tc.x, tc.y);
    if (!tileData?.features?.length) {
      return null;
    }

    const pixel = latLngToTilePixel(lat, lng, tc.z, tc.x, tc.y, CADASTRE_VT_TILE_EXTENT);
    const hit = findFeatureAtPoint(tileData.features, pixel.x, pixel.y, vt.featuresById, 50);
    if (!hit) {
      return null;
    }

    const orig = hit.originalFeature;
    const props = (orig?.properties ?? {}) as Record<string, unknown>;
    let parcel =
      (props['_parcel'] as CadastreParcelMapView | undefined) ??
      (((hit.tileFeature?.tags ?? hit.tileFeature) as { _parcel?: CadastreParcelMapView })?.['_parcel']) ??
      null;

    if (!parcel) {
      const idStr = String(props['parcelIdStr'] ?? '').trim();
      if (idStr) {
        parcel = this.parcels$.value.find((p) => String(p.id) === idStr) ?? null;
      }
    }

    return parcel?.id != null ? parcel : null;
  }

  private handleCadastreVtMapClick(ev: MapClickEvent): void {
    if (!this.cadastreVtPointer || !(this.parcels$.value?.length)) {
      return;
    }
    const parcel = this.queryParcelVtAt(ev.lat, ev.lng);
    if (!parcel) {
      return;
    }
    const center = this.getParcelRepresentativeLatLng(parcel);
    const latLng = center && typeof (center as { lat?: number }).lat === 'number'
      ? { lat: (center as { lat: number }).lat, lng: (center as { lng: number }).lng }
      : { lat: ev.lat, lng: ev.lng };
    this.parcelPolygonClick$.next({
      lat: latLng.lat,
      lng: latLng.lng,
      data: parcel,
    });
  }

  /** Converts WKT geometry into one or more outer rings as [lat, lng][] arrays. */
  private geometryJsonToLatLngRings(geometryJson?: string | null): [number, number][][] {
    if (!geometryJson?.trim()) {
      return [];
    }

    const trimmed = geometryJson.trim().toUpperCase();
    if (trimmed.startsWith('MULTIPOLYGON')) {
      const multi = extractCoordinatesFromWKT(geometryJson);
      return multi
        .map((polygonRings) => polygonRings[0])
        .filter((ring): ring is [number, number][] => Array.isArray(ring) && ring.length > 0);
    }

    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(geometryJson);
    return coordinates.length ? [coordinates] : [];
  }
}
